/**
 * The formatting buttons of a markdown text box (bold, italic, a link, a list) and the Preview/Write switch,
 * as in the post composer: useMarkdownEditing changes the text around the selection, MarkdownToolbar shows them.
 */
import React from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  InsertLink as InsertLinkIcon,
  FormatListBulleted as ListIcon,
  Visibility as PreviewIcon,
  Edit as WriteIcon,
} from '@mui/icons-material';

/** VALUE/SETVALUE: the text; INPUTREF: its textarea; ONEDIT: called after a change (e.g. to leave the preview). */
export function useMarkdownEditing(value, setValue, inputRef, onEdit) {
  // Wrap the selected text in markdown (bold, italic, a link); a link puts the cursor where the address goes
  const wrapSelection = (kind) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const selected = value.slice(start, end);
    const isUrl = /^https?:\/\/\S+$/.test(selected);
    let insert;
    let cursorFrom;
    let cursorTo;
    if (kind === 'link') {
      // a selected address becomes the link of a placeholder text, any other selection the text of the link
      const text = isUrl ? 'link text' : (selected || 'link text');
      const url = isUrl ? selected : 'https://';
      insert = `[${text}](${url})`;
      [cursorFrom, cursorTo] = isUrl || !selected ? [start + 1, start + 1 + text.length] : [start + text.length + 3, start + text.length + 3 + url.length];
    } else {
      const mark = kind === 'bold' ? '**' : '_';
      const text = selected || (kind === 'bold' ? 'bold text' : 'italic text');
      insert = `${mark}${text}${mark}`;
      [cursorFrom, cursorTo] = [start + mark.length, start + mark.length + text.length];
    }
    setValue(value.slice(0, start) + insert + value.slice(end));
    onEdit?.();
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(cursorFrom, cursorTo); } });
  };

  // Turn the selected lines (or the line with the cursor) into a "- " list, or back into plain lines
  const toggleList = () => {
    const el = inputRef.current;
    const selStart = el ? el.selectionStart : value.length;
    const selEnd = el ? el.selectionEnd : value.length;
    const start = value.lastIndexOf('\n', selStart - 1) + 1;
    const nextBreak = value.indexOf('\n', selEnd > selStart && value[selEnd - 1] === '\n' ? selEnd - 1 : selEnd);
    const end = nextBreak === -1 ? value.length : nextBreak;
    const lines = value.slice(start, end).split('\n');
    const isList = lines.every((l) => /^\s*[-*+]\s/.test(l) || !l.trim());
    const blank = lines.every((l) => !l.trim());
    const changed = blank ? '- ' : lines.map((l) => {      // an empty line starts a new item
      if (!l.trim()) return l;
      return isList ? l.replace(/^(\s*)[-*+]\s/, '$1') : `- ${l}`;
    }).join('\n');
    setValue(value.slice(0, start) + changed + value.slice(end));
    onEdit?.();
    // several lines stay selected; a single line keeps the cursor at its end, ready to type
    const [from, to] = lines.length > 1 ? [start, start + changed.length] : [start + changed.length, start + changed.length];
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(from, to); } });
  };

  return { wrapSelection, toggleList };
}

/** EDITING: from useMarkdownEditing; CHILDREN go between the formatting buttons and the Preview switch. */
export default function MarkdownToolbar({ editing, preview, onTogglePreview, children, sx }) {
  const button = (title, label, icon, onClick) => (
    <Tooltip title={title}>
      <span><IconButton size="small" aria-label={label} disabled={preview} onClick={onClick}>{icon}</IconButton></span>
    </Tooltip>
  );
  return (
    <Box display="flex" alignItems="center" gap={0.25} sx={{ mt: 0.5, flexWrap: 'wrap', minWidth: 0, ...sx }}>
      {button('Bold', 'Bold', <BoldIcon fontSize="small" />, () => editing.wrapSelection('bold'))}
      {button('Italic', 'Italic', <ItalicIcon fontSize="small" />, () => editing.wrapSelection('italic'))}
      {button("Link: select the words to link, then paste the page's address", 'Insert a link',
        <InsertLinkIcon fontSize="small" />, () => editing.wrapSelection('link'))}
      {button('Bulleted list', 'Bulleted list', <ListIcon fontSize="small" />, editing.toggleList)}
      <Box flex={1} />
      {children}
      <Button size="small" startIcon={preview ? <WriteIcon /> : <PreviewIcon />} onClick={onTogglePreview}
        sx={{ textTransform: 'none' }}>
        {preview ? 'Write' : 'Preview'}
      </Button>
    </Box>
  );
}
